/**
 * Was für eine Datei ist das - und wie sieht sie aus.
 *
 * ES STAND ZWEIMAL IM HAUS, UND DIE ZWEITE FASSUNG KONNTE WENIGER. Der
 * Dateireiter zeichnete eine Kachel mit Vorschaubild ODER einem gezeichneten
 * Blatt; die Umgebungsbilder in der Auftragsakte zeichneten dieselbe Kachel
 * noch einmal, nur ohne das Blatt - dort steht eine PDF als kaputtes
 * Bildzeichen da. Der Kommentar über der zweiten Fassung sagte es sogar:
 * „DASSELBE GITTER WIE IM DATEIREITER. Zwei Fassungen derselben Kachel laufen
 * beim ersten Nachtrag auseinander." **Das ist keine Begründung, sondern eine
 * Ankündigung** - und sie ist eingetreten, bevor jemand sie einlösen musste.
 *
 * DIESE DATEI TRÄGT NUR DIE REGELN UND KEIN JSX. Eine Datei mit JSX lässt
 * sich mit `--experimental-strip-types` nicht laden, und dann bliebe einem
 * Prüfstand nur die Textsuche - **eine Textsuche misst, dass etwas dasteht,
 * nicht, dass es stimmt.** Die Kachel selbst steht in `Dateikachel.tsx`, das
 * Fenster in `Dateifenster.tsx`. Siebzehnte Datei dieser Art nach `namen.ts`,
 * `terminraster.ts`, `klebeband.ts`, `kartenmass.ts`, `oberflaeche.ts`,
 * `kartenebene.ts`, `grenzpunkte.ts`, `handbuchmarken.ts`,
 * `tabellenbeschriftung.ts`, `zahlwoerter.ts`, `bruchstellen.ts`,
 * `anlasstext.ts`, `ereignisquelle.ts`, `technikfelder.ts`, `grabenlaenge.ts`
 * und `kartenstart.ts`.
 */

/**
 * Der Umriss des gezeichneten Blattes. Grau, und zwar in BEIDEN Fassungen
 * der Seite.
 *
 * ORIGINALTEXT MARVIN (27.08.2026)
 *   „die pdf oder andere dokumente müssen nicht blau oder rot umrandet
 *    werden. einfach nur das icon. auch wenn wir draufgeklickt haben ändert
 *    sich die farbe nicht."
 *
 * Das Blatt stand in einem Link, seine Linien waren `currentColor`, und die
 * Farbe eines Links ist BLAU - nach dem Anklicken die Besuchtfarbe. Damit sah
 * dieselbe Datei nach dem ersten Öffnen anders aus als ihre Nachbarin.
 * **EIN SINNBILD IST KEIN ZUSTAND:** es sagt „das ist eine PDF" und nicht
 * „hier war schon jemand".
 */
export const BLATTLINIE = '#8A94A6';

/** Die Farbe des Bandes im gezeichneten Blatt, je Endung. */
export const SYMBOLFARBE: Record<string, string> = {
  pdf: '#C0392B',
  doc: '#1F3864', docx: '#1F3864', odt: '#1F3864', rtf: '#1F3864',
  txt: '#5A6473',
  xls: '#1E7145', xlsx: '#1E7145', xlsm: '#1E7145', csv: '#1E7145',
  ods: '#1E7145',
  ppt: '#B7522B', pptx: '#B7522B',
  zip: '#8A6D1F', rar: '#8A6D1F', '7z': '#8A6D1F',
  dwg: '#4A3B77', dxf: '#4A3B77', dxb: '#4A3B77',
  heic: '#3A6E8F', mp4: '#3A6E8F', mov: '#3A6E8F',
};

/** Die Farbe für alles, was in der Liste nicht steht. */
export const SYMBOLFARBE_SONST = '#5A6473';

/** Die Endung, kleingeschrieben und ohne Punkt. Leer, wenn es keine gibt. */
export function endung(name: string): string {
  const n = String(name ?? '');
  const punkt = n.lastIndexOf('.');
  if (punkt <= 0 || punkt === n.length - 1) return '';
  return n.slice(punkt + 1).toLowerCase();
}

/** Die Farbe des Bandes für diesen Dateinamen. */
export function symbolfarbe(name: string): string {
  return SYMBOLFARBE[endung(name)] ?? SYMBOLFARBE_SONST;
}

/**
 * Was im Band steht. Höchstens vier Zeichen.
 *
 * OHNE ENDUNG STEHT NICHTS IM BAND. Ein leeres Band ist ehrlicher als ein
 * „DATE", das aus „Datei" abgeschnitten wurde.
 */
export function bandwort(name: string): string {
  const e = endung(name);
  return e ? e.toUpperCase().slice(0, 4) : '';
}

/**
 * BILDER, DIE EIN BROWSER WIRKLICH ZEIGT.
 *
 * `heic` steht mit Absicht NICHT darin, obwohl es ein Bild ist: kein Browser
 * zeigt es, und ein `<img>` darauf ergibt ein kaputtes Bildzeichen. Beim
 * Hochladen wird es seit dem 02.09.2026 zu JPEG gewandelt; im Bestand liegen
 * aus dem Umzug noch welche. **Ein Vorschaubild, das nicht kommt, ist
 * schlimmer als ein gezeichnetes Blatt: das Blatt sagt wenigstens, was für
 * eine Datei das ist.**
 */
const BILDENDUNGEN = new Set([
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif',
]);

/**
 * Ist das ein Bild, das sich anzeigen lässt?
 *
 * DER MIME-TYP ENTSCHEIDET, WO ES EINEN GIBT, und sonst die Endung. Er steht
 * an `datei.mime` und kann leer sein - eine Datei aus dem Umzug hat oft
 * keinen. Wer NUR den Mime-Typ fragt, zeigt für den halben Bestand kein
 * Vorschaubild; wer NUR die Endung fragt, glaubt einem Namen.
 */
export function istBild(mime?: string | null, name?: string | null): boolean {
  const m = String(mime ?? '');
  if (m) return /^image\//i.test(m) && !/heic|heif/i.test(m);
  return BILDENDUNGEN.has(endung(String(name ?? '')));
}

/** Ist das eine PDF? Dieselbe Regel wie beim Bild, nur ein anderer Typ. */
export function istPdf(mime?: string | null, name?: string | null): boolean {
  const m = String(mime ?? '');
  if (m) return /^application\/pdf$/i.test(m.split(';')[0]!.trim());
  return endung(String(name ?? '')) === 'pdf';
}

const VIDEOENDUNGEN = new Set(['mp4', 'mov', 'm4v', 'webm']);

/**
 * Ist das ein Video, das der Browser abspielt (.386)?
 *
 * ORIGINALTEXT MARVIN (16.10.2026)
 *   „ich muss die möglichkeit haben videos auch hochzuladen"
 */
export function istVideo(mime?: string | null, name?: string | null): boolean {
  const m = String(mime ?? '');
  if (m) return /^video\//i.test(m);
  return VIDEOENDUNGEN.has(endung(String(name ?? '')));
}

/**
 * WIE LÄSST SICH DIESE DATEI IM FENSTER ZEIGEN?
 *
 * ORIGINALTEXT MARVIN (08.09.2026, Punkt 7)
 *   „Anhänge in einem Fenster statt neuem Tab"
 *
 * Drei Ausgänge, und der dritte ist der wichtige: `null` heisst „im Fenster
 * ist davon nichts zu sehen". Eine Word-Datei in ein `<iframe>` zu legen
 * ergibt eine weisse Fläche oder einen Download, den niemand bestellt hat -
 * **eine Fläche, die leer bleibt, ist von einem kaputten Fenster nicht zu
 * unterscheiden.** Dort steht dann das gezeichnete Blatt und ein Weg zum
 * Herunterladen.
 */
export function imFenster(
  mime?: string | null, name?: string | null,
): 'bild' | 'pdf' | 'video' | null {
  if (istBild(mime, name)) return 'bild';
  if (istPdf(mime, name)) return 'pdf';
  if (istVideo(mime, name)) return 'video';
  return null;
}

/**
 * Eine Grösse, die ein Mensch liest.
 *
 * Dezimalkomma, wie überall, wo ein Mensch liest. Sie stand in `Dateien.tsx`
 * und wird ab jetzt auch an der Kachel im Postfach und in der Behördenpost
 * gebraucht.
 */
export function bytes(n: number): string {
  const z = Number(n);
  if (!Number.isFinite(z) || z < 0) return '';
  if (z < 1024) return `${z} B`;
  if (z < 1024 * 1024) return `${Math.round(z / 1024)} kB`;
  return `${(z / 1024 / 1024).toFixed(1)} MB`.replace('.', ',');
}

/**
 * Der Titel einer Kachel: Name und Grösse, wo es eine gibt.
 *
 * Eine Grösse, die niemand mitgeliefert hat, wird NICHT als „0 B" behauptet.
 */
export function kacheltitel(name: string, groesse?: number | null): string {
  const g = groesse === null || groesse === undefined ? '' : bytes(Number(groesse));
  return g ? `${name}, ${g}` : String(name ?? '');
}
